/**
 * Auth-aware button helpers for Puck components (Hero, ButtonBlock, WelcomeBlock).
 *
 * Public hub pages expose the visitor's auth state on window.modelData.userData
 * (serialized in _HubLayout.cshtml). In the Puck editor (admin pages) userData is
 * not set, so the state resolves to signed-out and editors always see the
 * signed-out version of the content.
 */

// Button visibility values (stored per button in CustomHeaderData / tab content)
export const BUTTON_VISIBILITY = {
  ALWAYS: "always",
  SIGNED_OUT: "signedOut",
  SIGNED_IN: "signedIn"
};

// Login/registration URLs. Buttons pointing at these are treated as
// signed-out-only when no explicit visibility has been configured, so headers
// authored before the visibility field existed behave correctly.
const SIGNED_OUT_URL_PATTERNS = [
  /\/portal\/login\//i,
  /\/portal\/register\//i,
  /\/account\/login\b/i,
  /\/account\/register\b/i
];

/**
 * Reads the visitor's auth state from window.modelData.
 * @returns {{isLoggedIn: boolean, displayName: string}}
 */
export function getUserAuthState() {
  const userData = (window.modelData && window.modelData.userData) || null;
  const isLoggedIn = !!(userData && (userData.loggedIn || userData.LoggedIn));
  const displayName =
    (userData && (userData.displayName || userData.DisplayName)) || "";
  return { isLoggedIn, displayName };
}

/**
 * Builds the participant profile URL for the current hub.
 * @returns {string} /Profile/{customerStringId}/{hubId}, or "#" when unknown
 */
export function getProfileUrl() {
  const modelData = window.modelData || {};
  const hubId = modelData.hubId || modelData.hub?.id || modelData.hub?.Id || 0;
  const customerStringId =
    modelData.customer?.stringId || modelData.customer?.StringId || "";
  return hubId && customerStringId
    ? `/Profile/${customerStringId}/${hubId}`
    : "#";
}

/**
 * Builds the sign-out URL, returning the visitor to the current page.
 * @returns {string} /Account/LogOff?returnUrl=<current url>
 */
export function getSignOutUrl() {
  const currentUrl = (window.location && window.location.href) || "";
  return `/Account/LogOff?returnUrl=${encodeURIComponent(currentUrl)}`;
}

/**
 * Resolves a button's effective visibility. Explicit configuration wins;
 * otherwise login/register links are inferred as signed-out-only.
 * @param {{visibility?: string, href?: string, url?: string}} button
 * @returns {string} one of BUTTON_VISIBILITY
 */
export function resolveButtonVisibility(button) {
  if (!button) {
    return BUTTON_VISIBILITY.ALWAYS;
  }

  if (
    button.visibility === BUTTON_VISIBILITY.SIGNED_OUT ||
    button.visibility === BUTTON_VISIBILITY.SIGNED_IN ||
    button.visibility === BUTTON_VISIBILITY.ALWAYS
  ) {
    return button.visibility;
  }

  const href = button.href || button.url || "";
  const isSignedOutLink = SIGNED_OUT_URL_PATTERNS.some((pattern) =>
    pattern.test(href)
  );

  return isSignedOutLink
    ? BUTTON_VISIBILITY.SIGNED_OUT
    : BUTTON_VISIBILITY.ALWAYS;
}

/**
 * Whether a button should render for the given auth state.
 * @param {object} button
 * @param {boolean} isLoggedIn
 * @returns {boolean}
 */
export function isButtonVisible(button, isLoggedIn) {
  const visibility = resolveButtonVisibility(button);
  if (visibility === BUTTON_VISIBILITY.SIGNED_OUT) {
    return !isLoggedIn;
  }
  if (visibility === BUTTON_VISIBILITY.SIGNED_IN) {
    return isLoggedIn;
  }
  return true;
}

/**
 * Filters Hero-style buttons ({label, href, variant}) by auth state. When the
 * visitor is signed in and one or more signed-out buttons (e.g. "Sign in",
 * "Register") were removed, two replacement buttons are appended: a "Sign out"
 * button followed by a button showing the visitor's display name linking to
 * their profile page. Within the (right-aligned) button group these render in
 * order, so "Sign out" sits to the left of the profile button.
 * @param {Array<object>} buttons configured buttons
 * @returns {Array<object>} buttons to render
 */
export function applyAuthToButtons(buttons) {
  if (!buttons || buttons.length === 0) {
    return buttons || [];
  }

  const { isLoggedIn, displayName } = getUserAuthState();
  const visible = buttons.filter((b) => isButtonVisible(b, isLoggedIn));

  if (isLoggedIn && visible.length < buttons.length) {
    const firstHidden = buttons.find((b) => !isButtonVisible(b, isLoggedIn));
    visible.push({
      label: "Sign out",
      href: getSignOutUrl(),
      variant: "outline-secondary"
    });
    visible.push({
      label: displayName || "My Profile",
      href: getProfileUrl(),
      variant: firstHidden?.variant || "primary"
    });
  }

  return visible;
}
